import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import {
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
  borderRadius,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UpArrowIcon from "@mui/icons-material/ArrowUpward";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  PolicyManagementRecord,
  BookedRowSource,
} from "entities/UIModel/PolicyManagementRecord";
import { useColumnSettings } from "utils/custom-hooks/useColumnSettings";
import { useCountyList } from "utils/custom-hooks/useCountyList";
import { usePolicyTypeOptions } from "utils/custom-hooks/usePolicyTypeOptions";
import { useFormPrefixOptions } from "utils/custom-hooks/useFormPrefixOptions";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { usePropertyTypeOptions } from "utils/custom-hooks/usePropertyTypeOptions";
import { usePolicyErrorTypeOptions } from "utils/custom-hooks/usePolicyErrorTypeOptions";
import { useDateTypeOptions } from "utils/custom-hooks/useDateTypeOptions";
import { useAsyncTextFilter } from "utils/custom-hooks/useAsyncTextFilter";
import { useTransCodeOptions } from "utils/custom-hooks/useTransCodeOptions";
import { useStateList } from "utils/custom-hooks/useStateList";
import {
  getFormNameListByText,
  getFileNumberListByText,
  getOriginalPolicyNumberListByText,
} from "utils/api/lookupApi";
import PolicyStatsBar from "../shared/PolicyStatsBar";
import BulkActionBar, { BulkButton } from "../shared/BulkActionBar";
import AdvancedSearchButton from "../../utils/AdvancedSearchButton";
import AdvancedSearchPanel, { parseIsoDate } from "../../utils/AdvancedSearchPanel";
import ExportButton from "../shared/ExportButton";
import PreCloseTable from "./PreCloseTable";
import { buildPcqColumns, PCQ_GRID_NAME } from "./pcqColumns";
import { EditFieldsModal, ForcePromoteModal } from "../modals";
import { PolicyManagementCounts } from "../counts/types";
import FileExportDialog from "controls/global/file-export-dialog";
import IosShareIcon from '@mui/icons-material/IosShare';
import { orderBy as _orderBy } from "lodash";
import { exportPolicyTransaction, PolicyTransactionCriteriaDto, PolicyTransactionExportCriteriaDto } from "utils/api/policyTransactionApi";
import BulkForcePromoteModal from "../shared/BulkActionModal";
import { HOLD_SCOPE, PIPActionType } from "utils/data/enum";
import { PERMISSIONS, StageType } from "utils/data/enum";
import { forcePromoteRecords } from "../shared/forcePromote";
import { handleBulkEditPolicyTransaction } from "../shared/bulkEdit";
import { handleBulkReturnPolicyTransaction } from "../shared/returnQueue";
import { bulkHoldPolicyRecords } from "../shared/hold";
import { bulkDeletePolicyRecords } from "../shared/delete";
import useSelectedRecords from "../shared/useSelectedRecords";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

const SAVE_BULK_EDIT_IN_PROGRESS_MESSAGE = "Please wait while saving your changes";

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const ChipRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  flexWrap: "wrap",
});

const ChipsSeparator = styled(Box)({
  width: 1,
  height: 22,
  backgroundColor: colors.grey09,
  marginLeft: gaps.xsmall2,
  marginRight: gaps.xsmall2,
});

const Chip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: gaps.xsmall1,
  padding: `${padding.xsmall1} ${padding.medium1}`,
  borderRadius: borderRadius.xlarge1,
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  cursor: "pointer",
  userSelect: "none",
  border: `1px solid ${colors.grey09}`,
  backgroundColor: colors.white,
  color: colors.grey11,
  whiteSpace: "nowrap",
  "&.active": {
    backgroundColor: colors.blue08,
    color: colors.blue02,
    borderColor: colors.blue01,
  },
  "&.passed.active": {
    backgroundColor: colors.white,
    color: colors.green,
    borderColor: colors.green,
  },
});

const ToolbarSpacer = styled(Box)({
  marginLeft: "auto",
  display: "flex",
  gap: gaps.small1,
  alignItems: "center",
});

const SelectedCount = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  color: colors.blue15,
  whiteSpace: "nowrap",
  marginLeft: gaps.small1,
});

type PcqChip =
  | "all"
  | "passed"
  | "forcePromoted"
  | "onHold"
  | "locked"
  | "edited"
  | "unedited"
  | "adjustments";

interface PreCloseTabProps {
  records: PolicyManagementRecord[];
  allRecords: PolicyManagementRecord[];
  onUpdateRecords: (updatedRecords: PolicyManagementRecord[]) => void;
  counts: PolicyManagementCounts;
  stateVals?: string[];
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  filters?: Record<string, string | string[] | number | null>;
  onFilterChange?: (name: string, value: string | string[] | number | null) => void;
  onResetFilters?: () => void;
  onChipCriteriaChange?: (criteria: Record<string, unknown>) => void;
  onAdvancedCriteriaChange?: (criteria: Record<string, unknown>) => void;
  applyEnabled?: boolean;
  onApplyFilters?: () => void;
  onRefresh?: () => void;
  sortColumn?: string;
  onSortChange?: (next: string) => void;
  userTimeZone?: string;
  // Global Search seed values to prefill matching column search boxes.
  headerFilterDefaults?: Record<string, string>;
}

function derivePcqChipCriteria(chips: Set<PcqChip>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (chips.has("forcePromoted")) out.IsForcePromoted = 1;
  if (chips.has("onHold")) out.IsOnHold = 1;
  if (chips.has("locked")) out.IsLocked = 1;
  if (chips.has("passed")) out.HasNoErrors = 1;
  if (chips.has("adjustments")) out.RecordTypeCode = "A";
  if (chips.has("edited") && !chips.has("unedited")) out.IsEdited = 1;
  if (chips.has("unedited") && !chips.has("edited")) out.IsEdited = 0;
  return out;
}

// Advanced Search panel → SP criteria. Stays pending until Apply Filters.
// serializeFilters() in usePolicyTransactionList joins arrays into a
// comma-separated string on the wire.
function derivePcqAdvancedCriteria(
  adv: Record<string, string | OptionRenderType[]>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const errors = adv.errorType;
  if (Array.isArray(errors) && errors.length > 0) {
    out.PolicyErrorTypeCodeList = errors.map((o) => o.value);
  }
  const dateType = typeof adv.dateType === "string" ? adv.dateType : "";
  if (dateType) out.DateTypeCode = dateType;
  const dateFrom = typeof adv.dateFrom === "string" ? adv.dateFrom : "";
  if (dateFrom) out.DateRangeStart = dateFrom;
  const dateTo = typeof adv.dateTo === "string" ? adv.dateTo : "";
  if (dateTo) out.DateRangeEnd = dateTo;
  return out;
}

const PCQ_PROTECTED_FIELDS: (keyof PolicyManagementRecord)[] = ["uniqueIdentifier"];

const PreCloseTab = ({
  records,
  allRecords,
  onUpdateRecords,
  counts,
  stateVals,
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  filters: filtersProp,
  onFilterChange,
  onResetFilters,
  onChipCriteriaChange,
  onAdvancedCriteriaChange,
  applyEnabled,
  onApplyFilters,
  onRefresh,
  sortColumn,
  onSortChange,
  userTimeZone,
  headerFilterDefaults,
}: PreCloseTabProps) => {
  const navigate = useNavigate();
  const handleRowAction = useCallback(
    (record: PolicyManagementRecord) => {
      navigate(`/policyManagement/product-details/${record.uniqueIdentifier}`, {
        state: { record },
      });
    },
    [navigate]
  );
  const [{ profileSettings }] = useProfileSettingsCache();
  const hasPMDeleteAccess = profileSettings.userPermissionItemTypes.includes(PERMISSIONS.PIP_PM_DELETE);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [skippedLockedIds, setSkippedLockedIds] = useState<string[]>([]);
  // Local, not lifted/persisted: a tab switch or chip click should always
  // collapse expanded rows, not preserve them.
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<PIPActionType>(PIPActionType.BULK_HOLD);
  const [openBulkActionModal, setOpenBulkActionModal] = useState<boolean>(false);

  const [fpOpen, setFpOpen] = useState(false);
  // Column-header filters live in the loader; see filtersProp on props.
  void filtersProp;
  const [activeChips, setActiveChips] = useState<Set<PcqChip>>(
    new Set<PcqChip>(["all"])
  );
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  // Bumped on Reset so the table (and all its uncontrolled column-header
  // inputs) remount with fresh empty state.
  const [resetCounter, setResetCounter] = useState(0);
  const [advancedValues, setAdvancedValues] = useState<
    Record<string, string | OptionRenderType[]>
  >({});
  const [openFileExportModal, setOpenFileExportModal] = useState<boolean>(false);

  const handleAdvancedChange = useCallback(
    (key: string, value: string | OptionRenderType[]) => {
      setAdvancedValues((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const selectedRecords = useSelectedRecords(records, selectedIds);

  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] =
    useAutomaticProgressDialogActions();

  // Chips, column-header filters, and advanced search all drive the server
  // criteria now — nothing to filter client-side.
  const filteredRecords = records;

  useEffect(() => {
    onChipCriteriaChange?.(derivePcqChipCriteria(activeChips));
  }, [activeChips, onChipCriteriaChange]);

  useEffect(() => {
    onAdvancedCriteriaChange?.(derivePcqAdvancedCriteria(advancedValues));
  }, [advancedValues, onAdvancedCriteriaChange]);

  const handleToggleRow = useCallback((record: PolicyManagementRecord) => {
    if (record.isLocked) return;
    setSelectedIds((prev) =>
      prev.includes(record.uniqueIdentifier)
        ? prev.filter((id) => id !== record.uniqueIdentifier)
        : [...prev, record.uniqueIdentifier]
    );
  }, []);

  const handleToggleAllRows = useCallback(() => {
    const selectableIds = filteredRecords
      .filter((r) => !r.isLocked)
      .map((r) => r.uniqueIdentifier);
    const lockedIds = filteredRecords
      .filter((r) => r.isLocked)
      .map((r) => r.uniqueIdentifier);

    const prevSelected = new Set(selectedIds);
    const deselectingPage =
      selectableIds.length > 0 &&
      selectableIds.every((id) => prevSelected.has(id));

    setSelectedIds((prev) => {
      if (deselectingPage) {
        const pageIds = new Set(selectableIds);
        return prev.filter((id) => !pageIds.has(id));
      }
      return [...prev, ...selectableIds.filter((id) => !prevSelected.has(id))];
    });

    // Locked rows are only "skipped" while the page they sit on is selected.
    setSkippedLockedIds((prev) => {
      if (deselectingPage) {
        const pageLocked = new Set(lockedIds);
        return prev.filter((id) => !pageLocked.has(id));
      }
      const prevSkipped = new Set(prev);
      return [...prev, ...lockedIds.filter((id) => !prevSkipped.has(id))];
    });
  }, [filteredRecords, selectedIds]);

  const handleFiltersChange = useCallback(
    (name: string, value: string | number | null) => {
      onFilterChange?.(name, value);
    },
    [onFilterChange]
  );

  const handleMultiSelectChange = useCallback(
    (
      e: { target: { name: string; value: OptionRenderType[] } },
      value: OptionRenderType[]
    ) => {
      const fieldName = e.target.name;
      const selectedValues = value.map((v) => v.value);
      onFilterChange?.(
        fieldName,
        selectedValues.length > 0 ? selectedValues : null
      );
    },
    [onFilterChange]
  );

  const handleBookedToggle = useCallback(
    (record: PolicyManagementRecord, next: BookedRowSource) => {
      const updated = allRecords.map((r) =>
        r.uniqueIdentifier === record.uniqueIdentifier
          ? { ...r, bookedRow: next }
          : r
      );
      onUpdateRecords(updated);
    },
    [allRecords, onUpdateRecords]
  );

  const handleRecordChange = useCallback(
    (next: PolicyManagementRecord) => {
      const updated = allRecords.map((r) =>
        r.uniqueIdentifier === next.uniqueIdentifier ? next : r
      );
      onUpdateRecords(updated);
    },
    [allRecords, onUpdateRecords]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSkippedLockedIds([]);
  }, []);

  const toggleChip = useCallback((chip: PcqChip) => {
    setActiveChips(new Set<PcqChip>([chip]));
    setExpandedRowIds([]);
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetFilters?.();
    clearSelection();
    setExpandedRowIds([]);
    setActiveChips(new Set<PcqChip>(["all"]));
    setAdvancedValues({});
    setResetCounter((c) => c + 1);
  }, [onResetFilters, clearSelection]);

  const selectedCount = selectedIds.length;
  const lockedNotSelectedCount = skippedLockedIds.length;

  const hasSelectedOnHold = useMemo(
    () => selectedRecords.some((r) => r.IsOnHold),
    [selectedRecords]
  );

  const holdableIds = useMemo(
    () =>
      selectedRecords
        .filter((r) => !r.IsOnHold)
        .map((r) => r.uniqueIdentifier),
    [selectedRecords]
  );
  const holdableCount = holdableIds.length;

  const { plainPromoteIds, forcePromoteIds } = useMemo(() => {
    const plain: number[] = [];
    const forced: number[] = [];
    selectedRecords.forEach((r) => {
      const target = (r.OpenErrorCount ?? 0) === 0 ? plain : forced;
      target.push(Number(r.uniqueIdentifier));
    });
    return { plainPromoteIds: plain, forcePromoteIds: forced };
  }, [selectedRecords]);

  // ---- Column state lifted from PreCloseTable ----
  const agentOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(filteredRecords.map((r) => r.AgentID ?? "").filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [filteredRecords]);

  // State column filter options: scoped to the page-level State picker when it
  // has a selection, otherwise the full lookup. Mirrors Close Queue.
  const { stateList } = useStateList();
  const stateOptions = useMemo(() => {
    if (stateVals && stateVals.length > 0) {
      return (stateList ?? []).filter((s) => stateVals.includes(s.value));
    }
    return stateList ?? [];
  }, [stateVals, stateList]);

  const productTypeOptions = usePolicyTypeOptions();
  const formPrefixOptions = useFormPrefixOptions();
  const transCodeOptions = useTransCodeOptions();
  const policyFormNameAsync = useAsyncTextFilter(getFormNameListByText);
  const fileNumberAsync = useAsyncTextFilter(getFileNumberListByText);
  const originalPolicyNumberAsync = useAsyncTextFilter(getOriginalPolicyNumberListByText);

  const recordTypeOptions = useRecordTypeOptions();

  const propertyTypeOptions = usePropertyTypeOptions();

  const countyOptions = useCountyList(stateVals);

  const policyErrorTypeOptions = usePolicyErrorTypeOptions();

  const dateTypeOptions = useDateTypeOptions();

  const advancedSections = useMemo(
    () => [
      {
        title: "Filters",
        fields: [
          {
            key: "errorType",
            label: "Error Types",
            type: "multi" as const,
            options: policyErrorTypeOptions,
            inline: true,
          },
        ],
      },
      {
        title: "Date",
        fields: [
          {
            key: "dateType",
            label: "Date Type",
            type: "select" as const,
            options: dateTypeOptions,
            inline: true,
          },
          {
            key: "dateFrom",
            label: "",
            type: "date" as const,
            maxDate: parseIsoDate(advancedValues.dateTo),
          },
          { key: "dateRangeSeparator", label: "—", type: "separator" as const },
          {
            key: "dateTo",
            label: "",
            type: "date" as const,
            minDate: parseIsoDate(advancedValues.dateFrom),
          },
        ],
      },
    ],
    [
      policyErrorTypeOptions,
      dateTypeOptions,
      advancedValues.dateFrom,
      advancedValues.dateTo,
    ]
  );

  const defaultColumns = useMemo(
    () =>
      buildPcqColumns({
        agentOptions,
        stateOptions,
        productTypeOptions,
        recordTypeOptions,
        propertyTypeOptions,
        countyOptions,
        formPrefixOptions,
        transCodeOptions,
        policyFormNameAsync,
        fileNumberAsync,
        originalPolicyNumberAsync,
        onFiltersChange: handleFiltersChange,
        onMultiSelectChange: handleMultiSelectChange,
        onBookedToggle: handleBookedToggle,
        onRowAction: handleRowAction,
        headerFilterDefaults,
      }),
    [
      agentOptions,
      stateOptions,
      productTypeOptions,
      recordTypeOptions,
      propertyTypeOptions,
      countyOptions,
      formPrefixOptions,
      transCodeOptions,
      policyFormNameAsync,
      fileNumberAsync,
      originalPolicyNumberAsync,
      handleFiltersChange,
      handleMultiSelectChange,
      handleBookedToggle,
      handleRowAction,
      headerFilterDefaults,
    ]
  );

  const {
    columns,
    hiddenColumns,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  } = useColumnSettings<PolicyManagementRecord>(
    PCQ_GRID_NAME,
    defaultColumns,
    PCQ_PROTECTED_FIELDS
  );

  const chooserOptions = useMemo<MenuColumnItem<PolicyManagementRecord>[]>(
    () =>
      defaultColumns
        .filter((c) => c.field !== "uniqueIdentifier")
        .map((c) => ({
          field: c.field,
          name: c.name,
          checked: !hiddenColumns.includes(c.field),
        })),
    [defaultColumns, hiddenColumns]
  );

  const handleColumnChecked = useCallback(
    (field: keyof PolicyManagementRecord, wasChecked: boolean) => {
      setColumnHidden(field, wasChecked);
    },
    [setColumnHidden]
  );


  const exportedColumns = useMemo(
    () => _orderBy(columns.filter(c => !hiddenColumns.includes(c.field)), "position").map(c => c.field),
    [columns, hiddenColumns]
  );
  const buildExportPayload = useCallback(
    (exportAll: boolean): PolicyTransactionExportCriteriaDto => {
      const baseCriteria: Partial<PolicyTransactionCriteriaDto> = {
        UserTimeZone: userTimeZone,
        RequestedPage: page,
        StageTypeCode: StageType.PRECLOSE,
        SortColumn: "receivedDate",
      };

      const searchCriteria = exportAll
        ? {
          ...baseCriteria,
          ListCount: 0,
          PolicyTransactionIDList: null,
        }
        : {
          ...baseCriteria,
          ListCount: selectedIds.length,
          PolicyTransactionIDList: selectedIds.join(", "),
        };

      return {
        ExportColumns: exportedColumns,
        SearchCriteria: searchCriteria,
      };
    },
    [exportedColumns, selectedIds, userTimeZone, page]
  );
  const handleFileExport = async () => {
    if (selectedIds.length > 0) {
      setOpenFileExportModal(true);
    } else {
      const payload: PolicyTransactionExportCriteriaDto = buildExportPayload(true);
      await exportPolicyTransaction(payload);
    }
  };

  const chipDefs: {
    key: PcqChip;
    label: string;
    group: 1 | 2 | 3 | 4;
    extraClass?: string;
  }[] = [
      { key: "all", label: `All (${counts.pcqAll})`, group: 1 },
      {
        key: "passed",
        label: `Passed (${counts.pcqPassed})`,
        group: 2,
        extraClass: "passed",
      },
      {
        key: "forcePromoted",
        label: `Force promoted (${counts.pcqForcePromoted})`,
        group: 2,
      },
      { key: "onHold", label: `On hold (${counts.pcqOnHold})`, group: 2 },
      { key: "locked", label: `Locked (${counts.pcqLocked})`, group: 2 },
      { key: "edited", label: `Edited (${counts.pcqEdited})`, group: 3 },
      { key: "unedited", label: `Unedited (${counts.pcqUnedited})`, group: 3 },
      {
        key: "adjustments",
        label: `Adjustment (${counts.pcqAdjustments})`,
        group: 4,
      },
    ];

  const handleBulkHoldPolicyTransaction = async (reason: string, holdScope: HOLD_SCOPE) => {
    if (holdableIds.length === 0) return;
    const ok = await bulkHoldPolicyRecords(
      holdableIds,
      reason,
      holdScope === HOLD_SCOPE.ALL_MATCHING
    );
    if (ok) {
      clearSelection();
      onRefresh?.();
    }
    setOpenBulkActionModal(false);
  }

  const handleBulkDeletePolicyTransaction = async (reason: string) => {
    if (selectedIds.length === 0) return;
    const ok = await bulkDeletePolicyRecords(selectedIds, reason);
    if (ok) {
      clearSelection();
      onRefresh?.();
    }
    setOpenBulkActionModal(false);
  }

  return (
    <TabContainer>
      <ChipRow>
        {chipDefs.map((c, idx) => {
          const prev = chipDefs[idx - 1];
          const sep = prev && prev.group !== c.group;
          return (
            <React.Fragment key={c.key}>
              {sep && <ChipsSeparator />}
              <Chip
                className={[
                  activeChips.has(c.key) ? "active" : "",
                  c.extraClass || "",
                ]
                  .join(" ")
                  .trim()}
                onClick={() => toggleChip(c.key)}
              >
                {c.label}
              </Chip>
            </React.Fragment>
          );
        })}
        {selectedCount > 0 && (
          <SelectedCount>
            {selectedCount} record{selectedCount !== 1 ? "s" : ""} selected
            {lockedNotSelectedCount > 0 &&
              `, ${lockedNotSelectedCount} locked, not selected`}
          </SelectedCount>
        )}
        <ToolbarSpacer>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="primary"
            disabled={!applyEnabled}
            onClick={onApplyFilters}
          >
            Apply Filters
          </ActionButton>
          <AdvancedSearchButton
            open={advancedSearchOpen}
            onClick={() => setAdvancedSearchOpen((o) => !o)}
          />
          <Button
            style={{
              backgroundColor: colors.white,
              opacity: 1,
              textTransform: "none",
            }}
            endIcon={<RefreshIcon />}
          >
            Re-Run Validation
          </Button>
          <Box sx={{ "& .MuiButton-root": { textTransform: "none" } }}>
            <ColumnsConfig
              options={chooserOptions}
              onChecked={handleColumnChecked}
              onShowAll={showAllColumns}
              onHideAll={hideAllColumns}
            />
          </Box>
          <ExportButton onClick={handleFileExport} />
        </ToolbarSpacer>
      </ChipRow>

      <AdvancedSearchPanel
        open={advancedSearchOpen}
        sections={advancedSections}
        values={advancedValues}
        onChange={handleAdvancedChange}
        onClear={() => setAdvancedValues({})}
      />

      <BulkActionBar open={selectedCount > 0} count={selectedCount}>
        <BulkButton
          icon={<EditOutlinedIcon />}
          label="Bulk Edit"
          onClick={() => setBulkEditOpen(true)}
        />
        <BulkButton icon={<RefreshIcon />} label="Re-Run Validation" primary />
        <BulkButton
          icon={<UpArrowIcon />}
          label={forcePromoteIds.length > 0 ? "Force Promote" : "Promote"}
          disabled={hasSelectedOnHold}
          onClick={() => setFpOpen(true)}
        />
        <BulkButton icon={<ReplyIcon />} label="Return to QR" onClick={() => { setBulkActionType(PIPActionType.BULK_RETURN); setOpenBulkActionModal(true); }} />
        <BulkButton
          icon={<PauseCircleOutlineIcon />}
          label="Hold"
          disabled={holdableCount === 0}
          onClick={() => { setBulkActionType(PIPActionType.BULK_HOLD); setOpenBulkActionModal(true); }}
        />
        {hasPMDeleteAccess && (
          <BulkButton
            icon={<DeleteOutlineIcon />}
            label="Delete"
            onClick={() => { setBulkActionType(PIPActionType.BULK_DELETE); setOpenBulkActionModal(true); }}
          />
        )}
      </BulkActionBar>

      <PreCloseTable
        key={resetCounter}
        records={filteredRecords}
        selectedIds={selectedIds}
        onToggleRow={handleToggleRow}
        onToggleAllRows={handleToggleAllRows}
        columns={columns}
        hiddenColumns={hiddenColumns}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        onRecordChange={handleRecordChange}
        onRefresh={onRefresh}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        onPageChange={onPageChange}
        totalRows={totalRows}
        totalPages={totalPages}
        isLoading={isLoading}
        sortColumn={sortColumn}
        onSortChange={onSortChange}
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        summaryComponent={
          <PolicyStatsBar records={filteredRecords} variant="pcq" />
        }
      />

      <EditFieldsModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        records={selectedRecords}
        showPcWarning
        onApply={(changes, note, newRemitSplitPcts) => {
          openAutomaticProgressDialog(SAVE_BULK_EDIT_IN_PROGRESS_MESSAGE);
          handleBulkEditPolicyTransaction(selectedIds, changes, note, newRemitSplitPcts)
            .then((ok) => {
              setBulkEditOpen(false);
              if (!ok) return;
              clearSelection();
              onRefresh?.();
            })
            .finally(() => closeAutomaticProgressDialog());
        }}
      />
      <ForcePromoteModal
        open={fpOpen}
        onClose={() => setFpOpen(false)}
        count={selectedCount}
        sourceLabel="Pre-Close Queue"
        destinationLabel="Close Queue"
        isForcePromote={forcePromoteIds.length > 0}
        onSubmit={async (reason) => {
          const calls: Promise<boolean>[] = [];
          if (forcePromoteIds.length > 0) {
            calls.push(forcePromoteRecords(forcePromoteIds, StageType.CLOSE, reason, 1));
          }
          if (plainPromoteIds.length > 0) {
            calls.push(forcePromoteRecords(plainPromoteIds, StageType.CLOSE, reason, 0));
          }
          const outcomes = await Promise.all(calls);
          if (outcomes.length === 0 || !outcomes.some(Boolean)) return;
          clearSelection();
          onRefresh?.();
        }}
      />
      <FileExportDialog
        open={openFileExportModal}
        onExport={async (exportAll) => {
          setOpenFileExportModal(false);
          const payload = buildExportPayload(exportAll);
          await exportPolicyTransaction(payload);
        }}
        onCancel={() => setOpenFileExportModal(false)}
        showSelectionPrompt={selectedIds?.some((s) => s)}
        selectionPrompt={"Include only the selected policies or all policies?"}
        icon={IosShareIcon}
      />
      <BulkForcePromoteModal
        open={openBulkActionModal}
        onClose={() => setOpenBulkActionModal(false)}
        recordCount={
          bulkActionType === PIPActionType.BULK_HOLD ? holdableCount : selectedIds.length
        }
        tabName="PRE CLOSE QUEUE"
        onConfirm={async (reason, holdScope) => {
          if (bulkActionType === PIPActionType.BULK_HOLD) {
            await handleBulkHoldPolicyTransaction(reason, holdScope ?? HOLD_SCOPE.ALL_MATCHING);
          }
          else if (bulkActionType === PIPActionType.BULK_RETURN) {
            const ok = await handleBulkReturnPolicyTransaction(selectedIds, reason, StageType.QUALITYREVIEW);
            if (ok) {
              setOpenBulkActionModal(false);
              onRefresh?.();
              clearSelection();
            }
          }
          else if (bulkActionType === PIPActionType.BULK_DELETE) {
            await handleBulkDeletePolicyTransaction(reason);
          }
        }}
        returnTabName="QUALITY REVIEW"
        actionType={bulkActionType}
      />
    </TabContainer>
  );
};

export default PreCloseTab;
